"use client";

import { performanceLog } from "@/utilities/debug";
import { getLocalStorageItem } from "@/utilities/storage";
import { usePathname } from "next/navigation";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

interface IPerformanceContextProps {
  checkTimeElapsed: (time: number, description?: string) => void;
  setBenchmarkTime: (description?: string) => void;
}

interface IPerformanceContextProviderProps {
  children: ReactNode;
}

const PerformanceContext = createContext<IPerformanceContextProps | null>(null);

const PerformanceProvider = (props: IPerformanceContextProviderProps) => {
  const { children } = props;
  const pathName = usePathname();
  const [benchmarkTime, setBenchmarkTime] = useState<number>(Date.now());

  const checkTimeElapsed = (time: number, description?: string) => {
    performanceLog(
      `Time elapsed: ${time - benchmarkTime} ms${description ? ` - ${description}` : ""}`,
    );
  };

  const setBenchmarkTimeInternal = (description?: string) => {
    const time = Date.now();

    performanceLog(
      `Setting new benchmark. Time elapsed: ${time - benchmarkTime} ms${description ? ` - ${description}` : ""}`,
    );

    setBenchmarkTime(time);
  };

  useEffect(() => {
    setBenchmarkTimeInternal(`After navigating to ${pathName}`);
  }, [pathName]);

  return (
    <PerformanceContext.Provider
      value={{ checkTimeElapsed, setBenchmarkTime: setBenchmarkTimeInternal }}
    >
      {children}
    </PerformanceContext.Provider>
  );
};

export { PerformanceProvider, PerformanceContext };

export const usePerformance = () => {
  const context = useContext(PerformanceContext);

  if (!context) {
    throw new Error(
      "usePerformance must be used within an PerformanceProvider",
    );
  }

  return context;
};
